import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import { TrashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import PaginationV2 from "../../../components/PaginationV2";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import ColumnSearch from "../../../components/ColumnSearch";
import useDialogStore from "../../../stores/useDialogStore";
import Button from "../../../components/Button";
import TonerModelForm from "./TonerModelForm";

export default function TonerModels() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const currentPage = parseInt(searchParams.get("page") || "1");
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();

  // Sorting config
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  const user = getUser();

  // Handle column search
  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
  };

  // Handle column sort
  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    let currentOrder = params.get("order");
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }
    navigate(`${location.pathname}?${params.toString()}`);
    setSortConfig({ key: column, direction });
  };

  const {
    data: toners,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["toners", currentPage, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      params.set("page", currentPage);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/toners?${params.toString()}`
      );
      // Depending on API structure, usually response.data holds the paginated object or data array.
      // Laravel pagination structure: response.data.data has items, response.data.pagination has metadata OR response.data directly paginated
      return response.data;
    },
  });

  // Fetch brands for reference
  const { data: brands } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-brands`
      );
      return response.data.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/toners/${id}`
      ),
    onSuccess: () => {
      refetch();
      closeDialog();
      toast.success("Toner deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete toner");
    },
  });

  const editCellMutation = useMutation({
    mutationFn: ({ id, field, value }) =>
      axiosInstance.put(
        `${window.REACT_APP_API_ENDPOINT}/toners/${id}`,
        {
          [field]: value,
        }
      ),
    onSuccess: () => {
      refetch();
      toast.success("Toner updated successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to update toner");
    },
  });

  const handleDelete = async (id) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error("Delete toner error:", error);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands?.find((b) => b.id === brandId);
    return brand ? brand.name : "Unknown Brand";
  };
  const getBrandOptions = () => {
    return (
      brands?.map((brand) => ({
        id: brand.id,
        name: brand.name,
      })) || []
    );
  };
  const getColorOptions = () => [
    { id: "Black", name: "Black" },
    { id: "Cyan", name: "Cyan" },
    { id: "Magenta", name: "Magenta" },
    { id: "Yellow", name: "Yellow" }
  ];

  const handleClearFilters = () => {
    navigate(location.pathname);
    // Force refetch when filters are cleared
    queryClient.invalidateQueries(["toners"]);
  };

  const tonerItems = toners?.data || toners?.items || [];
  const paginationData = toners?.pagination || toners || {};

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user?.name || "User"}</h3>
          </div>
        </div>
        <TonerModelForm updateData={() => refetch()} />
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">Toners</h4>
                <div className="flex flex-col gap-2 lg:flex-row items-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Part Number"
                            columnKey="part_number"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Part Number..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Machine Model"
                            columnKey="machine_model"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Machine Model..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Toner Model"
                            columnKey="toner_model"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Toner Model..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Color"
                            columnKey="color"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Color..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Price"
                            columnKey="price"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Price..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Toner Price"
                            columnKey="toner_price"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Toner Price..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Weight"
                            columnKey="weight"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Weight..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Units"
                            columnKey="units"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Units..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Brand"
                            columnKey="device_brand_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search Brand..."
                          />
                        </div>
                      </th>

                      {user?.role === "SuperAdmin" && (
                        <th
                          scope="col"
                          className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                        >
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={13} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : tonerItems.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center">
                          <NoData message="No toners found" />
                        </td>
                      </tr>
                    ) : (
                      tonerItems.map((toner, index) => (
                        <tr
                          key={toner.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap py-5 pl-8 text-sm ">
                            {toner.id}
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={toner.part_number}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "part_number",
                                    value: newValue.value,
                                  });
                                }}
                                inputType="text"
                                maxLength={50}
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={toner.machine_model}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "machine_model",
                                    value: newValue.value,
                                  });
                                }}
                                inputType="text"
                                maxLength={50}
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={toner.toner_model}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "toner_model",
                                    value: newValue.value,
                                  });
                                }}
                                inputType="text"
                                maxLength={50}
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableSelectCell
                                value={toner.color}
                                displayValue={toner.color}
                                options={getColorOptions()}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "color",
                                    value: newValue.value,
                                  });
                                }}
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={toner.price}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "price",
                                    value: newValue.value !== "" ? newValue.value : 0,
                                  });
                                }}
                                inputType="number"
                                className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={toner.toner_price}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "toner_price",
                                    value: newValue.value !== "" ? newValue.value : null,
                                  });
                                }}
                                inputType="number"
                                className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={toner.weight}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "weight",
                                    value: newValue.value !== "" ? newValue.value : 0,
                                  });
                                }}
                                inputType="number"
                                className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={toner.units}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "units",
                                    value: newValue.value !== "" ? newValue.value : 0,
                                  });
                                }}
                                inputType="number"
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableSelectCell
                                value={toner.device_brand_id}
                                displayValue={getBrandName(
                                  toner.device_brand_id
                                )}
                                options={getBrandOptions()}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: toner.id,
                                    field: "device_brand_id",
                                    value: parseInt(newValue.value),
                                  });
                                }}
                                maxLength={17}
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>

                          {user?.role === "SuperAdmin" && (
                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                              <div className="flex items-center pl-8">
                                <Button
                                  variant="outline-danger"
                                  size="icon"
                                  icon={<TrashIcon className="h-4 w-4" />}
                                  onClick={() => {
                                    openDialog({
                                      title: "Delete Toner",
                                      content: (
                                        <div className="flex flex-col gap-4">
                                          <p className="text-lg">
                                            Are you sure you want to delete toner{" "}
                                            <span className="font-bold">
                                              {toner.part_number}
                                            </span>
                                            ?
                                            <br />
                                            <span className="text-red-500 text-sm font-bold">
                                              (This action cannot be undone)
                                            </span>
                                          </p>
                                          <div className="flex items-center justify-end gap-2">
                                            <Button
                                              variant="light"
                                              size="sm"
                                              onClick={() => closeDialog()}
                                            >
                                              Cancel
                                            </Button>
                                            <Button
                                              variant="danger"
                                              size="sm"
                                              icon={
                                                <TrashIcon className="h-4 w-4" />
                                              }
                                              onClick={() => {
                                                handleDelete(toner.id);
                                                closeDialog();
                                              }}
                                            >
                                              Delete
                                            </Button>
                                          </div>
                                        </div>
                                      ),
                                    });
                                  }}
                                  className="p-1"
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={paginationData.current_page || paginationData.currentPage}
                last_page={paginationData.last_page || paginationData.lastPage}
                next_page_url={paginationData.next_page_url || (paginationData.current_page < paginationData.last_page ? 'next' : null)}
                prev_page_url={paginationData.prev_page_url || (paginationData.current_page > 1 ? 'prev' : null)}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
